// Pièces d'un dossier : pièces typées avec verdict (conforme, non conforme avec motif visible par
// l'usager), pièces attendues par la version, rattachement d'un document déposé comme pièce typée.
import { LIBELLES_VERDICT, type DocumentResume, type DossierDetail } from '@cc/contrats/ui-metier';
import { Bouton, Champ, Selection } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { Pieces } from '../Fiches';
import commun from '../../../commun/commun.module.css';
import styles from '../metier.module.css';

function Verdict({ dossierId, pieceId, actuel }: { dossierId: string; pieceId: string; actuel: string }) {
  const [v, setV] = useState(actuel === 'non_examinee' ? '' : actuel);
  const [motif, setMotif] = useState('');
  const maj = useAction({
    executer: (verdict: string) => requete(`/dossiers/${dossierId}/pieces/${pieceId}/verdict`, { methode: 'PUT', corps: verdict === 'non_conforme' ? { verdict, motif: motif.trim() || 'Pièce non conforme' } : { verdict } }),
    succes: 'Verdict enregistré.',
    invalider: [['dossiers', 'fiche', dossierId]],
  });
  return (
    <span className={styles.pile}>
      <Selection
        libelle="Verdict"
        value={v}
        disabled={maj.enCours}
        onChange={(e) => {
          setV(e.target.value);
          if (e.target.value === 'conforme') maj.lancer('conforme');
        }}
        optionVide="Non examinée"
        options={[{ valeur: 'conforme', libelle: 'Conforme' }, { valeur: 'non_conforme', libelle: 'Non conforme' }]}
        erreur={maj.messageErreur}
      />
      {v === 'non_conforme' && actuel !== 'non_conforme' ? (
        <>
          <Champ libelle="Motif de la non-conformité (visible par l'usager)" value={motif} onChange={(e) => setMotif(e.target.value)} required />
          <Bouton variante="secondaire" petit chargement={maj.enCours} disabled={motif.trim().length < 3} onClick={() => maj.lancer('non_conforme')}>
            Enregistrer le verdict
          </Bouton>
        </>
      ) : null}
    </span>
  );
}

/** Rattache un document déjà déposé sur le dossier comme pièce typée. */
function RattacherPiece({ dossier }: { dossier: DossierDetail }) {
  const [documentId, setDocumentId] = useState('');
  const [type, setType] = useState('');
  const docs = useQuery({
    queryKey: ['documents', 'objet', 'dossier', dossier.id],
    queryFn: ({ signal }) => requete<{ elements: DocumentResume[] }>('/documents', { signal, parametres: { objet_type: 'dossier', objet_id: dossier.id } }).then((r) => r.elements),
    retry: false,
  });
  const rattachement = useAction({
    executer: () => requete(`/dossiers/${dossier.id}/pieces`, { methode: 'POST', corps: { document_id: documentId, type_piece: type } }),
    succes: 'Pièce rattachée au dossier.',
    invalider: [['dossiers', 'fiche', dossier.id]],
    apres: () => {
      setDocumentId('');
      setType('');
    },
  });
  const deja = new Set(dossier.pieces.map((p) => p.document_id));
  const libres = (docs.data ?? []).filter((d) => !deja.has(d.id));
  if (libres.length === 0 || dossier.pieces_attendues.length === 0) return null;
  return (
    <div className={commun.ligne} style={{ alignItems: 'flex-end' }}>
      <Selection libelle="Document déposé" value={documentId} optionVide="Choisir un document" options={libres.map((d) => ({ valeur: d.id, libelle: d.titre }))} onChange={(e) => setDocumentId(e.target.value)} />
      <Selection libelle="Type de pièce" value={type} optionVide="Choisir le type" options={dossier.pieces_attendues.map((p) => ({ valeur: p.type, libelle: p.libelle }))} onChange={(e) => setType(e.target.value)} />
      <Bouton variante="secondaire" chargement={rattachement.enCours} disabled={!documentId || !type} onClick={() => rattachement.lancer(undefined)}>
        Rattacher comme pièce
      </Bouton>
      {rattachement.messageErreur ? <span className={commun.na}>{rattachement.messageErreur}</span> : null}
    </div>
  );
}

export function PiecesDossier({ dossier: d, peutModifier }: { dossier: DossierDetail; peutModifier: boolean }) {
  const conformes = d.pieces.filter((p) => p.verdict === 'conforme').length;
  const fournis = new Set(d.pieces.map((p) => p.type_piece));
  const manquantes = d.pieces_attendues.filter((p) => p.obligatoire && !fournis.has(p.type));
  return (
    <div className={styles.pile}>
      <p className="cc-etiquette">
        Pièces justificatives : {conformes} sur {d.pieces.length} conforme{conformes > 1 ? 's' : ''}
        {manquantes.length > 0 ? `, ${manquantes.length} pièce${manquantes.length > 1 ? 's' : ''} obligatoire${manquantes.length > 1 ? 's' : ''} manquante${manquantes.length > 1 ? 's' : ''}` : ''}
      </p>
      {d.pieces.length > 0 ? (
        <ul className={styles.listePieces}>
          {d.pieces.map((p) => {
            const attendue = d.pieces_attendues.find((a) => a.type === p.type_piece);
            return (
              <li key={p.id}>
                <span>
                  <strong>{attendue?.libelle ?? p.type_piece}</strong> : {p.titre}
                  {p.motif ? <span className={commun.na}> ({p.motif})</span> : null}
                  {p.examinee_le ? <span className={commun.na}>, examinée le {date(p.examinee_le)}</span> : null}
                </span>
                {peutModifier ? <Verdict dossierId={d.id} pieceId={p.id} actuel={p.verdict} /> : <span>{LIBELLES_VERDICT[p.verdict] ?? p.verdict}</span>}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className={commun.na}>Aucune pièce typée rattachée.</p>
      )}
      {d.pieces_attendues.length > 0 ? (
        <p className={commun.na}>
          Pièces attendues par la version {d.demarche.numero_version} : {d.pieces_attendues.map((p) => `${p.libelle}${p.obligatoire ? ' (obligatoire)' : ''}${fournis.has(p.type) ? ', fournie' : ', manquante'}`).join(' ; ')}
        </p>
      ) : null}
      {peutModifier ? <RattacherPiece dossier={d} /> : null}
      <Pieces objetType="dossier" objetId={d.id} peutDeposer={peutModifier} />
    </div>
  );
}
